import React, { useContext, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import Typography from 'kingpin/atoms/Typography';

import MetricCacheContext from 'contexts/MetricCacheContext';
import CardContext from 'contexts/CardContext';
import useGetMetricIdsFromVis from 'hooks/useGetMetricIdsFromVis';

import FilterPlates from '../FilterPlates';
import isV5ChartDef from '../../types/visTypeCheckers/isV5ChartDef';
import V5Gadget from '../V5Gadget';
import ErrorBoundary from '../Common/ErrorBoundary';
import Row from '../Common/Row';
import ComparisonSelector from '../Report/ComparisonSelector';
import { Label } from '../MyAccount/Profile/styles';
import ReportSelector from './ReportSelector';
import DateInput from '../DateInput';
import BonusPeriodModeToggle from '../BonusPeriodModeToggle';
import isSingleMetricDateMatrix from '../../types/visTypeCheckers/isSingleMetricDateMatrix';
import SingleMetricDateMatrixContainer from '../SingleMetricDateMatrix';
import isGauge from '../../types/visTypeCheckers/isGauge';
import Gauge from '../Gauge';
import isRemindersGadget from '../../types/visTypeCheckers/isRemindersGadget';
import RemindersGadget from '../RemindersGadget';
import WeekStartsOnOverrideSelector from '../Report/WeekStartsOnOverrideSelector';
import WeekStartsOnOverrideContext from '../../contexts/WeekStartsOnOverrideContext';
import isRankingMatrix from '../../types/visTypeCheckers/isRankingMatrix';
import RankingMatrix from '../RankingMatrix';
import Loading from '../Loading/Loading';
import Card from '../Common/Card';
import EntityFilterToggle from '../EntityFilterToggle';
import FeatureGate, { FEATURE_GATES } from '../FeatureGate';
import isPaceMatrix from '../../types/visTypeCheckers/isPaceMatrix';
import PaceMatrix from '../PaceMatrix';
import DashboardGadgetDefaultFiltersToggle from './DashboardGadgetDefaultFiltersToggle';
import IgnoreDashboardFiltersToggle from './IgnoreDashboardFiltersToggle';
import isGroupVsRemainder from '../../types/visTypeCheckers/isGroupVsRemainder';
import GroupVsRemainder from '../GroupVsRemainder';
import isEntityInformationTable from '../../types/visTypeCheckers/isEntityInformationTable';
import EntityInformationTable from '../EntityInformationTable';
import { Z_INDEX } from '../../constants';
import Tooltip from '../Tooltip';
import Icon from '../../kingpin/atoms/Icon';

import EntityAppUsageAlert from '../Dashboard/EntityAppUsageAlert';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 24px;
  position: relative;
`;

const ConfigureDashboardGadget = ({
  chartDef,
  reportDrillDownId,
  setReportDrillDownId,
  boardDrillDownId,
  setBoardDrillDownId,
  isBonusPeriodMode,
  onIsBonusPeriodModeChanged,
  selectedBonusPeriod,
  isValid,
  weekStartsOnOverride,
  setWeekStartsOnOverride,
  isEntityFilterEnabled,
  setIsEntityFilterEnabled,
  isDashboardFiltersIgnored,
  setIsDashboardFiltersIgnored,
}: {
  chartDef?: VisualisationDefinition;
  reportDrillDownId?: string;
  setReportDrillDownId: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
  boardDrillDownId?: string;
  setBoardDrillDownId: React.Dispatch<React.SetStateAction<string | undefined>>;
  isBonusPeriodMode: boolean;
  onIsBonusPeriodModeChanged: (newStatus: boolean) => void;
  selectedBonusPeriod?: BonusPeriod | BonusTypes.BonusPeriod;
  isValid: boolean;
  weekStartsOnOverride?: WeekStartsOn;
  setWeekStartsOnOverride: React.Dispatch<
    React.SetStateAction<WeekStartsOn | undefined>
  >;
  isEntityFilterEnabled: boolean;
  setIsEntityFilterEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  isDashboardFiltersIgnored: boolean;
  setIsDashboardFiltersIgnored: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { requestMetricsByIds, isIdResolved } = useContext(MetricCacheContext);
  const getMetricIds = useGetMetricIdsFromVis();

  const metricIds = useMemo(
    () => (chartDef ? getMetricIds(chartDef) : []),
    [chartDef, getMetricIds],
  );

  useEffect(() => {
    if (metricIds.length > 0) {
      requestMetricsByIds(metricIds);
    }
  }, [metricIds, requestMetricsByIds]);

  const metricsResolved =
    metricIds.length === 0 || metricIds.every((id) => isIdResolved(id));

  return (
    <Wrapper
      onClick={(event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        event.stopPropagation();
      }}
    >
      <div>
        <div>
          {/* Misc */}
          <div style={{ marginBottom: 24 }}>
            <Row style={{ marginBottom: 4 }}>
              {/* Date Scope */}
              <div style={{ marginRight: 16 }}>
                <Label>
                  <Typography.Body type={'Label'}>Date Scope</Typography.Body>
                </Label>
                <Row centerAlign>
                  <div>
                    <DateInput
                      isBonusPeriodMode={isBonusPeriodMode}
                      isDashboardCard
                    />
                  </div>
                </Row>
              </div>
              {/* Reporting Week*/}
              {!isRankingMatrix(chartDef) && (
                <div style={{ marginRight: 16 }}>
                  <Label>
                    <Typography.Body type={'Label'}>
                      Reporting Week
                    </Typography.Body>
                  </Label>
                  <WeekStartsOnOverrideSelector
                    setWeekStartsOnOverride={setWeekStartsOnOverride}
                    weekStartsOnOverride={weekStartsOnOverride}
                  />
                </div>
              )}
              {/* Compare With */}
              {!isRankingMatrix(chartDef) && (
                <div style={{ marginRight: 16 }}>
                  <Label>
                    <Typography.Body type={'Label'}>
                      Compare With
                    </Typography.Body>
                  </Label>
                  <ComparisonSelector
                    isPaceForm={isPaceMatrix(chartDef)}
                    isSingleMetricDateMatrixForm={isSingleMetricDateMatrix(
                      chartDef,
                    )}
                    isOnlyPreviousAllowed={isBonusPeriodMode}
                  />
                </div>
              )}
              {/* Drilldown */}
              <div style={{ marginRight: 16 }}>
                <Label>
                  <Typography.Body type={'Label'}>Drilldown</Typography.Body>
                </Label>
                <ReportSelector
                  reportId={reportDrillDownId}
                  setReportId={setReportDrillDownId}
                  boardId={boardDrillDownId}
                  setBoardId={setBoardDrillDownId}
                />
              </div>
              <div style={{ marginLeft: 'auto' }}>
                <EntityAppUsageAlert />
              </div>
            </Row>
            <FeatureGate featureName={FEATURE_GATES.DRIVER_BONUS}>
              <Row>
                <BonusPeriodModeToggle
                  selectedBonusPeriod={selectedBonusPeriod}
                  isBonusPeriodMode={isBonusPeriodMode}
                  onIsBonusPeriodModeChanged={onIsBonusPeriodModeChanged}
                />
              </Row>
            </FeatureGate>
          </div>

          {!isEntityInformationTable(chartDef) && (
            <div style={{ marginBottom: 8 }}>
              <EntityFilterToggle
                isEntityFilterEnabled={isEntityFilterEnabled}
                setIsEntityFilterEnabled={setIsEntityFilterEnabled}
              />
            </div>
          )}

          <div style={{ marginBottom: 8 }}>
            <DashboardGadgetDefaultFiltersToggle chartDef={chartDef} />
          </div>

          <div style={{ marginBottom: 8 }}>
            <IgnoreDashboardFiltersToggle
              isDashboardFiltersIgnored={isDashboardFiltersIgnored}
              setIsDashboardFiltersIgnored={setIsDashboardFiltersIgnored}
            />
          </div>

          {/* Filters */}
          <Row
            style={{
              zIndex: Z_INDEX.CONFIGURE_DASHBOARD_GADGET,
              flexWrap: 'wrap',
              marginBottom: 32,
            }}
          >
            {/* Drilldowns */}
            {!isRemindersGadget(chartDef) && (
              <div style={{ marginRight: 16 }}>
                <Label>
                  <Row centerAlign style={{ gap: 4 }}>
                    <Typography.Body type={'Label'}>
                      Run Time Filters
                    </Typography.Body>
                    <Tooltip
                      content={
                        <div style={{ maxWidth: 280 }}>
                          Filters always applied when this gadget renders. When
                          a viewer clicks through to a popup report, these
                          filters are passed into the popup as its initial
                          drilldowns — visible and dismissible inside the popup.
                        </div>
                      }
                    >
                      <Icon icon="info" color={'var(--color-grey-3)'} />
                    </Tooltip>
                  </Row>
                </Label>
                <Row>
                  <FilterPlates isDashboardCard label={'Add Run Time Filter'} />
                </Row>
              </div>
            )}

            {/* Scope */}
            <div>
              <Label>
                <Row centerAlign style={{ gap: 4 }}>
                  <Typography.Body type={'Label'}>Scope</Typography.Body>
                  <Tooltip
                    content={
                      <div style={{ maxWidth: 280 }}>
                        Silent baseline filters always applied to this gadget.
                        Never shown to viewers and cannot be dismissed. When a
                        viewer clicks through to a popup report, scope is passed
                        along as a background filter.
                      </div>
                    }
                  >
                    <Icon icon="info" color={'var(--color-grey-3)'} />
                  </Tooltip>
                </Row>
              </Label>
              <Row>
                <FilterPlates isScope isDashboardCard label="Add Scope" />
              </Row>
            </div>
          </Row>
        </div>
        <Card style={{ position: 'relative', height: 400 }}>
          {!chartDef ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <Typography.Body type={'Body 14'}>
                Select a gadget type to preview
              </Typography.Body>
            </div>
          ) : !metricsResolved ? (
            <Loading />
          ) : (
            <WeekStartsOnOverrideContext.Provider
              value={{ weekStartsOnOverride }}
            >
              <CardContext.Provider
                value={{
                  chartDefinition: chartDef,
                  i: 'na',
                  w: 10,
                  h: 10,
                  isForConfigureDashboardGadgetPopup: true,
                }}
              >
                <ErrorBoundary>
                  <>
                    {isV5ChartDef(chartDef) && (
                      <V5Gadget
                        chartDefinition={chartDef}
                        useSmallNoDataMessage={false}
                        key={
                          chartDef.simpleGridConfig ? undefined : chartDef.id
                        }
                      />
                    )}
                    {isSingleMetricDateMatrix(chartDef) && (
                      <SingleMetricDateMatrixContainer
                        gadget={chartDef}
                        key={chartDef.id}
                      />
                    )}
                    {isGauge(chartDef) && <Gauge gauge={chartDef} />}
                    {isRemindersGadget(chartDef) && isValid && (
                      <RemindersGadget gadget={chartDef} key={chartDef.id} />
                    )}
                    {isRankingMatrix(chartDef) && (
                      <RankingMatrix gadget={chartDef} key={chartDef.id} />
                    )}
                    {isPaceMatrix(chartDef) && (
                      <PaceMatrix gadget={chartDef} key={chartDef.id} />
                    )}
                    {isGroupVsRemainder(chartDef) && (
                      <GroupVsRemainder gadget={chartDef} key={chartDef.id} />
                    )}
                    {isEntityInformationTable(chartDef) && (
                      <EntityInformationTable
                        gadget={chartDef}
                        key={Math.random()}
                      />
                    )}
                  </>
                </ErrorBoundary>
              </CardContext.Provider>
            </WeekStartsOnOverrideContext.Provider>
          )}
        </Card>
      </div>
    </Wrapper>
  );
};

export default ConfigureDashboardGadget;
